import type { Query as CubeQuery } from "@cubejs-client/core";
import * as dateFns from "date-fns";
import { format, parseISO, subDays, subHours } from "date-fns";
import {
  type PresetDateRangeItem,
  RANGE_NAMES,
  TIME_BUFFER,
} from "@/constants/date.constants";
import { QUERY_DATE_FORMAT } from "@/constants/date.constants";
import type { DateRange, DateRangeType } from "@/types/common";
import type { SelectedRange } from "@/types/data";

/**
 * @module date.helpers
 */

/**
 * Returns a time range based on current date.
 *
 * @param time the live time, default to now
 * @param buffer plus/minus offset in milliseconds, default to 5 hours
 */
export const getCurrentLiveTimeRange = (
  time = new Date(),
  buffer: number = TIME_BUFFER
): { from: string; to: string } => {
  const from = new Date(time.getTime() - buffer);
  const to = new Date(time.getTime() + buffer);

  return {
    from: dateToDateString(from),
    to: dateToDateString(to),
  };
};

/**
 * Returns an ISO format date string.
 * Same as Date.toISOString except milliseconds not included.
 *
 * @param date
 * @returns an ISO date string
 */
export const dateToDateString = (date: Date): string => {
  return dateFns.formatRFC3339(date);
};

/**
 * Returns a Date for the current settlement period start time
 * that falls after a given time.
 *
 * Settlement periods:
 * - can vary in length
 * - assumed to be > 1 min, < 24 hours
 * - fall on midnight (i.e. whole number in a day)
 * - the end of one period is the same time as the start of the following one
 *
 * @param time  a given Date
 * @param settlementPeriodLength  SP duration in minutes
 */
export const getCurrentSettlementPeriodStart = (
  time: Date,
  settlementPeriodLength: number
): Date => {
  const currentTime = new Date(time);
  const startOfDay = dateFns.startOfDay(currentTime);
  const minutesToday = dateFns.differenceInMinutes(currentTime, startOfDay);

  const pastSettlementsCount = Math.floor(
    minutesToday / settlementPeriodLength
  );

  return dateFns.addMinutes(
    startOfDay,
    pastSettlementsCount * settlementPeriodLength
  );
};

/**
 * Returns a Date for the next settlement period start time
 * that falls after a given time.
 *
 * @param time  a given Date
 * @param settlementPeriodLength  SP duration in minutes
 */
export const getNextSettlementPeriodStart = (
  time: Date,
  settlementPeriodLength: number
): Date => {
  const previousPeriod = getCurrentSettlementPeriodStart(
    time,
    settlementPeriodLength
  );

  return dateFns.addMinutes(previousPeriod, settlementPeriodLength);
};

/**
 * Returns a Date for the next settlement period end time
 * that falls after a given time.
 *
 * @param time  a given Date
 * @param settlementPeriodLength  SP duration in minutes
 */
export const getNextSettlementPeriodEnd = (
  time: Date,
  settlementPeriodLength: number
): Date => {
  const nextSPStart = getNextSettlementPeriodStart(
    time,
    settlementPeriodLength
  );

  return dateFns.addMinutes(nextSPStart, settlementPeriodLength);
};

/**
 * Compares two number ranges returning true if the periods overlap.
 *
 * e.g. the following is true
 *    0123456789
 * a: ---++++---
 * b: ------+++-
 */
export const isRangeIntersecting = (
  a: DateRange<number>,
  b: DateRange<number>
) => {
  const start = Math.max(a.from, b.from);
  const end = Math.min(a.to, b.to);

  return end - start > 0;
};

/**
 * Return ranges that intersect with the base range, otherwise undefined.
 */
export const anyRangeIntersecting = (
  base: DateRange<number>,
  evalList: DateRange<number>[]
): DateRange<number>[] | undefined => {
  const intersects = evalList.filter((item) => isRangeIntersecting(base, item));
  return intersects.length ? intersects : undefined;
};

/**
 * Calculates the earliest valid `from` time for a contract for a given asset.
 *
 * @param isBM  is the asset a BMU
 * @param settlementPeriod  the settlement period
 * @param from  [optional] the current or from time
 */
export const getMinValidFromTime = (
  isBM: boolean,
  settlementPeriod: number,
  from = new Date()
): Date => {
  if (isBM) {
    return getNextSettlementPeriodStart(
      dateFns.addHours(from, 1),
      settlementPeriod
    );
  }
  return getCurrentSettlementPeriodStart(from, settlementPeriod);
};

/**
 * Performs time validation on energy contract blocks
 * taking into account gate closure for BMUs
 *
 * @returns an error message or undefined if valid
 */
export const validateTimeBlocks = (
  isBM: boolean,
  settlementPeriod: number,
  timeBlocks: DateRange<string | Date>[]
): string | undefined => {
  if (!timeBlocks.length) {
    return "No time periods selected.";
  }

  const [first] = timeBlocks;
  if (dateFns.isBefore(first.to, new Date())) {
    return "The selected time cannot end in the past.";
  }

  const minValidFromTime = getMinValidFromTime(isBM, settlementPeriod);
  if (dateFns.isBefore(first.from, minValidFromTime)) {
    return isBM
      ? "The selected time cannot start before gate closure."
      : "The selected time cannot start before the current settlement period.";
  }

  return undefined;
};

const validRangeTypes: DateRangeType[] = [
  "yesterday",
  "today",
  "last7days",
  "custom",
];

export const isRangeType = (t: unknown): t is DateRangeType => {
  return validRangeTypes.includes(t as DateRangeType);
};

/**
 * Formats a date range for CubeJS queries
 *
 * @param selectedRange - The selected date range with from, to dates and type
 * @returns A tuple of [from, to] dates formatted as 'yyyy-MM-dd'
 */
export const getCubeFormatDateRange = (
  selectedRange: SelectedRange | undefined
): [string, string] => {
  const { from, to } = selectedRange ?? {};
  if (!from || !to) return ["", ""];

  return [
    format(parseISO(from), QUERY_DATE_FORMAT),
    format(parseISO(to), QUERY_DATE_FORMAT),
  ];
};

/**
 * Returns a date range based on the number of days and granularity.
 * For hourly granularity with 1 day, returns ISO format with time.
 * For other cases, returns formatted dates using QUERY_DATE_FORMAT.
 *
 * @param days - Number of days to go back from current date
 * @param granularity - Time granularity ('hour', 'day', etc.)
 * @returns A tuple of [startDate, endDate] as strings
 */
export const getDateRange = (
  days: number,
  granularity: string
): [string, string] => {
  const end = new Date();
  let start: Date;

  if (granularity === "hour" && days === 1) {
    // For 24 hours, go back exactly 24 hours
    start = subHours(end, 24);
    // Use ISO format with time for hourly granularity
    return [start.toISOString(), end.toISOString()];
  } else {
    // For other periods, use the existing logic
    // Subtract days-1 to exclude today (since today's data is incomplete)
    start = subDays(end, days - 1);
    return [format(start, QUERY_DATE_FORMAT), format(end, QUERY_DATE_FORMAT)];
  }
};

type DateLike = string | number | Date;

// Helper to get difference in days between two dates
export const getDateDiffInDays = (from: DateLike, to: DateLike): number => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  return Math.ceil(
    (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)
  );
};
// Helper to format x-axis title for a date range
export const getXAxisTitleForRange = (
  from: DateLike | undefined,
  to: DateLike | undefined,
  fallback = "unknown"
): string => {
  const fromStr = from ? format(new Date(from), "dd/MM/yyyy") : fallback;
  const toStr = to ? format(new Date(to), "dd/MM/yyyy") : fallback;
  return fromStr === toStr ? fromStr : `${fromStr} - ${toStr}`;
};

/**
 * Returns the timezone abbreviation (BST/GMT) for a given date based on UK timezone rules.
 * Uses the Intl API to determine the correct timezone based on the date.
 *
 * @param date - The date to get timezone for. Can be a Date object, timestamp, or date string
 * @returns The timezone abbreviation (BST/GMT) or empty string for invalid inputs
 *
 * @example
 * getUKTimezoneAbbr(new Date()) // Returns "BST" during summer time, "GMT" otherwise
 * getUKTimezoneAbbr("2023-12-25") // Returns "GMT"
 * getUKTimezoneAbbr(1684930800000) // Returns timezone for the timestamp
 * getUKTimezoneAbbr("invalid") // Returns ""
 * getUKTimezoneAbbr(null) // Returns ""
 */
export const getUKTimezoneAbbr = (date: DateLike): string => {
  if (!date) {
    return "";
  }

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return "";
    }

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      timeZoneName: "short",
    }).formatToParts(dateObj);

    return parts.find((part) => part.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};

export const toDateInstance = (value?: string) =>
  value ? new Date(value) : undefined;

/**
 * Generates a list of preset date range items for use in date range selection components.
 *
 * @param dateRange - The currently selected date range
 */
export const presetDateRangeList = (
  dateRange: SelectedRange
): PresetDateRangeItem[] => {
  return [
    {
      value: "live",
      dateRange: {
        from: toDateInstance(dateRange?.from),
        to: toDateInstance(dateRange?.to),
      },
    },
    { value: "efaDay", name: RANGE_NAMES.efaDay },
    { value: "efaDayPlusOne", name: RANGE_NAMES.efaDayPlusOne },
    { value: "today" },
    { value: "yesterday" },
    { value: "tomorrow" },
  ];
};

/**
 * Converts a date string to a timestamp, handling timezone appropriately based on query settings.
 * If timezone is UTC, appends 'Z' to ensure the date is interpreted in UTC.
 *
 * @param dateStr - The date string to convert
 * @param query - The cube query containing timezone settings
 * @returns The timestamp in milliseconds since epoch
 */
export const getTimezoneAwareTimestamp = (
  dateStr: string,
  query?: CubeQuery
): number => {
  const tzAwareDate = query?.timezone === "UTC" ? `${dateStr}Z` : dateStr;
  return new Date(tzAwareDate).getTime();
};

/**
 * Creates a UTC date from timezone-aware date components.
 * Takes a date that represents a time in a specific timezone and creates
 * a UTC date with the same date/time components (e.g., London 09:27 -> UTC 09:27).
 * This matches the "London time as UTC" format used by the data keys.
 *
 * @param zonedDate - A date representing a time in a specific timezone
 * @returns A UTC date with the same date/time components as the input
 */
export const createUTCFromZonedDate = (zonedDate: Date): Date => {
  return new Date(
    Date.UTC(
      zonedDate.getFullYear(),
      zonedDate.getMonth(),
      zonedDate.getDate(),
      zonedDate.getHours(),
      zonedDate.getMinutes(),
      zonedDate.getSeconds(),
      zonedDate.getMilliseconds()
    )
  );
};

/**
 * Creates a UTC date from date components (year, month, day) and time components (hours, minutes).
 * Uses date components from the provided date and overrides the time with specified hours/minutes.
 * This matches the "London time as UTC" format used by the data keys.
 *
 * @param date - A date to extract year, month, and day components from
 * @param hours - The hour component (0-23)
 * @param minutes - The minute component (0-59)
 * @returns A UTC date with the date components from the input date and the specified time
 */
export const createUTCFromDateAndTime = (
  date: Date,
  hours: number,
  minutes: number
): Date => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      0,
      0
    )
  );
};

/**
 * Gets the UTC start of today (midnight UTC) as a Date object.
 * This extracts UTC date components from the current date and creates a UTC date
 * at midnight UTC for today.
 *
 * @returns A UTC date representing midnight UTC of today
 */
export const getUTCStartOfToday = (): Date => {
  const now = new Date();
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );
};

/**
 * Converts a UTC date (where UTC components represent London time) to a local date for display.
 * Takes a UTC date where the UTC components represent a specific timezone (e.g., London time)
 * and creates a local date with those same components for display purposes.
 * This is the inverse of createUTCFromZonedDate.
 *
 * Used primarily for calendar date picker components (e.g., CalendarDateInput) which require
 * a local Date object to display correctly. The UTC date's components are copied to a local date
 * so the calendar shows the same time values regardless of the user's timezone.
 *
 * @param date - A UTC date where UTC components represent the desired display time
 * @returns A local date with the same date/time components, or undefined if input is null/undefined
 *
 * @example
 * // If data key is "2024-01-15T12:00:00.000Z" (representing London 12:00)
 * // This creates a local date that displays as 12:00 in the calendar picker
 * const displayDate = convertUTCToLocalForDisplay(utcDate);
 * // Used in CalendarDateInput: <CalendarDateInput value={displayDate} />
 */
export const convertUTCToLocalForDisplay = (
  date: Date | null | undefined
): Date | undefined => {
  if (!date) return undefined;

  // Get the UTC components (which represent the desired display time)
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  // Create a date in local timezone with these same components
  // This ensures the calendar displays the same time as the timeline
  return new Date(year, month, day, hours, minutes, seconds);
};

/**
 * Formats a Date as an ISO string with normalized milliseconds (.000Z).
 * This ensures consistent formatting to match data key formats.
 *
 * @param date - The date to format
 * @returns ISO string with normalized milliseconds (e.g., "2024-01-01T12:00:00.000Z")
 */
export const formatISOWithNormalizedMillis = (date: Date): string => {
  return date.toISOString().replace(/\.\d{3}Z$/, ".000Z");
};

/**
 * Returns start and end of day timestamps for a given date
 * @param date - The date to get start/end timestamps for
 * @returns Object containing start and end timestamps for the day
 */
export const getDayBoundaryTimestamps = (
  date: Date
): { startOfDay: Date; endOfDay: Date } => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  return { startOfDay, endOfDay };
};

/**
 * Calculates the settlement period number for a given timestamp.
 * Settlement periods are 30 minutes each, starting at midnight UK time.
 * There are 48 settlement periods in a day (numbered 1-48).
 *
 * @param timestamp - The timestamp to calculate the settlement period for
 * @param settlementPeriodLength - SP duration in minutes (default 30)
 * @returns The settlement period number (1-48)
 */
export const getSettlementPeriodNumber = (
  timestamp: number | Date,
  settlementPeriodLength = 30
): number => {
  const date = new Date(timestamp);
  const startOfDay = dateFns.startOfDay(date);
  const minutesToday = dateFns.differenceInMinutes(date, startOfDay);

  // Calculate which settlement period this falls into (0-based)
  const settlementPeriodIndex = Math.floor(
    minutesToday / settlementPeriodLength
  );

  // Return 1-based settlement period number (1-48)
  return settlementPeriodIndex + 1;
};
